import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { UIStateProps, uiState } from '../../../../atoms/uiAtom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useGetIndex, useGetIndexedVolumes } from '../../../../hooks/meters';

import BottomSheet from '../../../common/BottomSheet';
import { ReactComponent as BurgerIcon } from '../../../../assets/burger.svg';
import { ConsumptionUnitEnum } from '../../../../enum/ConsumptionUnitEnum';
import { DateTime } from 'luxon';
import { EanURL } from '../../../../common/eanUtils';
import EmptyState from '../../../common/EmptyState';
import { ReactComponent as EyeIcon } from '../../../../assets/eye.svg';
import { ReactComponent as EyeSlashIcon } from '../../../../assets/eye-slash.svg';
import { ReactComponent as ImportantIcon } from '../../../../assets/important.svg';
import { ReactComponent as InformationIcon } from '../../../../assets/information.svg';
import { Link } from 'react-router-dom';
import MeterIndexCard from './MeterIndexCard';
import ModalInformation from '../../../modals/ModalInformation';
import { NumberUtils } from '../../../../common/numberUtils';
import { SDPCategoryEnum } from '../../../../enum/SDPCategoryEnum';
import { SdpResponse } from '../../../../models/SdpResponse';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import { routes } from '../../../../routes';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

export interface MeterIndexProps {
  sdp?: SdpResponse | null;
}
export default function MeterIndex({ sdp }: MeterIndexProps) {
  const { t, i18n } = useTranslation();
  const {
    isFetching: isFetchingLastIndexVolumes,
    data: dataLastIndexVolumes,
    isError: isErrorLastIndexVolumes,
  } = useGetIndexedVolumes({
    sdpId: sdp?.id,
  });
  const { isFetching, data, isError } = useGetIndex({
    sdpId: sdp?.id,
    startDate:
      sdp?.initialLoadDate ?? DateTime.now().setZone('Europe/Brussels').toJSDate().toISOString(),
    endDate: DateTime.now().setZone('Europe/Brussels').toJSDate().toISOString(),
  });

  const [isOpenModalIndexInformation, setIsOpenModalIndexInformation] = useState(false);

  const hasMixerEncodeIndex = useMemo(() => {
    if (sdp?.mrMonth && sdp.category === SDPCategoryEnum.YMR) {
      const now = DateTime.now().setZone('Europe/Brussels');
      const periodStartDate = now
        .set({ month: sdp?.mrMonth })
        .plus({ days: 15 })
        .setZone('Europe/Brussels');
      const periodEndDate = now
        .set({ month: sdp?.mrMonth })
        .plus({ months: 2, days: 15 })
        .setZone('Europe/Brussels');
      if (periodStartDate <= now && periodEndDate >= now) {
        return true;
      }
      return false;
    }
    return false;
  }, [sdp?.category, sdp?.mrMonth]);

  const setUiState = useSetRecoilState<UIStateProps>(uiState);

  useEffect(() => {
    setUiState((prev) => ({ ...prev, hasError: isError }));
  }, [setUiState, isError]);

  const [rowsTableIndex, setRowsTableIndex] = useState<
    {
      direction: string;
      startDate?: string;
      endDate: string;
      period?: number;
      totalConsumption: number;
      totalInjection?: number;
    }[]
  >([]);

  const [columnVisibility, setColumnVisibility] = useState({
    direction: true,
    startDate: true,
    endDate: true,
    period: true,
    totalConsumption: true,
    totalInjection: true,
    action: true,
  });

  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState(false);

  useEffect(() => {
    if (isFetching === false && isError === false && data?.data) {
      setRowsTableIndex(
        data!.data!.map<{
          direction: string;
          startDate?: string;
          endDate: string;
          period?: number;
          totalConsumption: number;
          totalInjection?: number;
          averageConsumption?: number;
          averageInjection?: number;
        }>((index) => {
          const startDate =
            index.startDate && DateTime.fromISO(index.startDate).setZone('Europe/Brussels');
          const endDate =
            index.endDate && DateTime.fromISO(index.endDate).setZone('Europe/Brussels');
          const period =
            endDate && startDate ? endDate.diff(startDate, 'days').days + 1 : undefined;
          const averageConsumption = period
            ? index.consumption?.total
              ? index.consumption?.total / period
              : undefined
            : undefined;
          const averageInjection = period
            ? index.injection?.total
              ? index.injection?.total / period
              : undefined
            : undefined;

          return {
            direction:
              index.injection != null
                ? `${t('common.sampling')}/${t('common.injection')}`
                : `${t('common.sampling')}`,
            startDate: index.startDate,
            endDate: index.endDate,
            period,
            totalConsumption: index.consumption?.total,
            totalInjection: index?.injection?.total,
            averageConsumption,
            averageInjection,
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data, i18n.language, isError, isFetching, t]);

  const hasTotalInjectionColumn = useMemo(() => {
    if (!data?.data || isError) return false;
    return data?.data.some((index) => index.injection != null);
  }, [data?.data, isError]);

  const handleClickBurgerMenu = () => {
    setIsOpenBottomSheet(true);
  };

  const columnHelper = createColumnHelper<{
    direction: string;
    startDate?: string;
    endDate: string;
    period: number;
    totalConsumption: number;
    totalInjection?: number;
    averageConsumption?: number;
    averageInjection?: number;
    action?: string;
  }>();
  const columns = [
    columnHelper.accessor('direction', {
      header: t('overview.meter.detail.index.type'),
      cell: (row) => row.getValue(),
    }),
    columnHelper.accessor('startDate', {
      id: 'startDate',
      header: t('common.start'),
      sortingFn: (a: any, b: any) => {
        return (
          DateTime.fromISO(a.original.startDate).toMillis() -
          DateTime.fromISO(b.original.startDate).toMillis()
        );
      },

      cell: (row) =>
        row.getValue() &&
        DateTime.fromISO(row.getValue()!)
          .setLocale(i18n.language)
          .setZone('Europe/Brussels')
          .toFormat('dd/MM/yyyy'),
    }),
    columnHelper.accessor('endDate', {
      header: t('common.end'),
      cell: (row) =>
        DateTime.fromISO(row.getValue())
          .setLocale(i18n.language)
          .setZone('Europe/Brussels')
          .toFormat('dd/MM/yyyy'),
    }),
    columnHelper.accessor('period', {
      header: t('common.period'),
      cell: (row) =>
        row.getValue() !== null &&
        row.getValue() !== undefined &&
        `${row.getValue().toFixed()} ${t('common.day').charAt(0)}`,
    }),
    columnHelper.accessor('totalConsumption', {
      header: t('overview.meter.detail.index.total.sampling'),
      cell: (row) =>
        row.getValue() !== null &&
        row.getValue() !== undefined &&
        ConsumptionUnitEnum.formatValue(ConsumptionUnitEnum.KWH, row.getValue()),
    }),
    columnHelper.accessor('averageConsumption', {
      header: t('overview.meter.detail.index.average.sampling'),
      cell: (row) =>
        row.getValue() !== null &&
        row.getValue() !== undefined &&
        `${ConsumptionUnitEnum.formatValue(
          ConsumptionUnitEnum.KWH,
          row.getValue()
        )}/${t('common.day').charAt(0)}`,
    }),
    columnHelper.accessor('totalInjection', {
      header: t('overview.meter.detail.index.total.injection'),
      cell: (row) =>
        row.getValue() !== null &&
        row.getValue() !== undefined &&
        ConsumptionUnitEnum.formatValue(ConsumptionUnitEnum.KWH, row.getValue()),
    }),
    columnHelper.accessor('averageInjection', {
      header: t('overview.meter.detail.index.average.injection'),
      cell: (row) =>
        row.getValue() !== null &&
        row.getValue() !== undefined &&
        `${ConsumptionUnitEnum.formatValue(
          ConsumptionUnitEnum.KWH,
          row.getValue()
        )}/${t('common.day').charAt(0)}`,
    }),
    columnHelper.accessor('action', {
      header: () => <BurgerIcon role="button" onClick={handleClickBurgerMenu} className="icon" />,
      meta: {
        className: 'action',
      },
      cell: (info) => (
        <Link
          className="btn btn--primary"
          to={`${routes.withArgs(
            `${routes.METERS.path}${routes.METERS.children.METERS_DETAIL.path}${routes.METERS.children.METERS_INDEX_DETAIL.path}`,
            {
              ':id': EanURL.encrypt(sdp?.id!) as string,
              ':indexId': info.row.index.toString(),
            }
          )}${window.location.search}`}
        >
          {t('common.detail')}
        </Link>
      ),
    }),
  ].filter((column) => {
    return column.accessorKey === 'totalInjection' || column.accessorKey === 'averageInjection'
      ? hasTotalInjectionColumn
      : true;
  });

  const table = useReactTable({
    data: rowsTableIndex as any,
    state: {
      columnVisibility,
    },
    columns,
    initialState: {
      sorting: [
        {
          id: 'startDate',
          desc: true, // sort by name in descending order by default
        },
      ],
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleClickVisibility = ({
    column,
    visibility,
  }: {
    column: string;
    visibility: boolean;
  }) => {
    setColumnVisibility((prevState) => ({
      ...prevState,
      [column]: visibility,
    }));
  };

  const handleClickInfo = () => {
    setIsOpenModalIndexInformation(true);
  };

  const handleClickModalIndexInformation = () => {
    setIsOpenModalIndexInformation(false);
  };

  const hasLastIndexVolumes = useMemo(() => {
    const lastIndexVolumes = dataLastIndexVolumes?.data[0];
    if (!lastIndexVolumes) {
      return false;
    } else {
      const startPeriod = DateTime.now().setZone('Europe/Brussels').minus({ days: 30 }).toMillis();
      const endPeriod = DateTime.now().setZone('Europe/Brussels').toMillis();
      const volumePeriod = DateTime.fromISO(lastIndexVolumes.period)
        .setZone('Europe/Brussels')
        .toMillis();
      if (volumePeriod > startPeriod && volumePeriod < endPeriod) return true;
    }

    return false;
  }, [dataLastIndexVolumes?.data]);

  const renderLastIndexVolumes = () => {
    return (
      <div className="meter-index__last-volume__block">
        <MeterIndexCard
          title={`${t('common.sampling')} - ${t('common.full.hour')}`}
          isLoading={isFetchingLastIndexVolumes}
          value={dataLastIndexVolumes?.data[0]?.consumption?.day ?? 0}
          unit={ConsumptionUnitEnum.getSymbol(ConsumptionUnitEnum.KWH)}
          type="consumption"
        />
        <MeterIndexCard
          title={`${t('common.sampling')} - ${t('common.mid.hour')}`}
          isLoading={isFetchingLastIndexVolumes}
          value={dataLastIndexVolumes?.data[0]?.consumption?.night ?? 0}
          unit={ConsumptionUnitEnum.getSymbol(ConsumptionUnitEnum.KWH)}
          type="consumption"
        />
        <MeterIndexCard
          title={`${t('common.injection')} - ${t('common.full.hour')}`}
          isLoading={isFetchingLastIndexVolumes}
          value={dataLastIndexVolumes?.data[0]?.injection?.day ?? 0}
          unit={ConsumptionUnitEnum.getSymbol(ConsumptionUnitEnum.KWH)}
          type="injection"
        />
        <MeterIndexCard
          title={`${t('common.injection')} - ${t('common.mid.hour')}`}
          isLoading={isFetchingLastIndexVolumes}
          value={dataLastIndexVolumes?.data[0]?.injection?.night ?? 0}
          unit={ConsumptionUnitEnum.getSymbol(ConsumptionUnitEnum.KWH)}
          type="injection"
        />
      </div>
    );
  };

  return (
    <section className="meter-index">
      {data?.data.length === 0 && !isFetching ? (
        <Fragment>
          <div className="meter-index__encode-block">
            {hasMixerEncodeIndex && (
              <a
                className="btn btn--primary meter-index__encode-block__link"
                target="_blank"
                rel="noreferrer"
                href={t('overview.meter.detail.index.encode.link.uri')}
              >
                {t('overview.meter.detail.index.encode.link.label')}
              </a>
            )}
          </div>
          <EmptyState message={t('common.no.data')} icon={<ImportantIcon />} />
        </Fragment>
      ) : isFetching || isFetchingLastIndexVolumes || isError || isErrorLastIndexVolumes ? (
        <Fragment>
          {(isFetchingLastIndexVolumes || isErrorLastIndexVolumes) && renderLastIndexVolumes()}
          <div className="d-flex justify-content-between ">
            <Skeleton count={1} width={200} height={42} />
            <Skeleton containerClassName="meter-index__skeleton-encode" count={1} height={36} />
          </div>
          <div className="meter-index__table-data">
            <table>
              <thead>
                <tr>
                  {Array.from(Array(columns.length).keys()).map((_, index) => (
                    <th key={index} className="table-cell">
                      <Skeleton count={1} height={16} width={NumberUtils.randomInteger(50, 300)} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from(Array(10).keys()).map((_, index) => (
                  <tr key={index}>
                    {Array.from(Array(10).keys()).map((_, index) => (
                      <td key={index} className="table-cell">
                        <Skeleton
                          count={1}
                          height={16}
                          width={NumberUtils.randomInteger(50, 200)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      ) : (
        <Fragment>
          {hasLastIndexVolumes ? (
            <div className="meter-index__last-volume">
              <h3>
                <div className="meter-index__last-volume__title">
                  {t('overview.meter.detail.index.last.volume.title')}
                  <InformationIcon
                    className="ms-2 icon icon--action icon--info"
                    onClick={handleClickInfo}
                  />
                  <p className="meter-index__last-volume__title__caption">
                    {DateTime.fromISO(dataLastIndexVolumes?.data[0].period!)
                      .setLocale(i18n.language)
                      .setZone('Europe/Brussels')
                      .toFormat('dd/MM/yyyy')}
                  </p>
                </div>
              </h3>
            </div>
          ) : null}
          <div className="meter-index__encode-block">
            {hasMixerEncodeIndex && (
              <a
                className="btn btn--primary meter-index__encode-block__link"
                target="_blank"
                rel="noreferrer"
                href={t('overview.meter.detail.index.encode.link.uri')}
              >
                {t('overview.meter.detail.index.encode.link.label')}
              </a>
            )}
          </div>
          {hasLastIndexVolumes ? renderLastIndexVolumes() : null}
          <div className="meter-index__table-data">
            <h3>{t('overview.meter.detail.index.table.title')}</h3>
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={classNames(
                          'table-cell',
                          (header.column.columnDef.meta as any)?.className ?? ''
                        )}
                      >
                        <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={classNames(
                          'table-cell',
                          (cell.column.columnDef.meta as any)?.className ?? ''
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
      <BottomSheet
        isOpen={isOpenBottomSheet}
        onClose={() => setIsOpenBottomSheet(false)}
        title={t('common.column')}
      >
        <ul className="meter-index__column-list">
          {table.getAllColumns().map(
            (column) =>
              (column.columnDef.meta as any)?.className !== 'action' && (
                <li className="bottom-sheet__content__list__item" key={column.id}>
                  <Fragment>
                    {column.columnDef.header as any}
                    {column.getIsVisible() === true && (
                      <EyeIcon
                        role="button"
                        className="icon bottom-sheet__content__list__item__action"
                        onClick={() =>
                          handleClickVisibility({
                            column: column.id,
                            visibility: false,
                          })
                        }
                      />
                    )}
                    {column.getIsVisible() === false && (
                      <EyeSlashIcon
                        role="button"
                        className="icon bottom-sheet__content__list__item__action"
                        onClick={() =>
                          handleClickVisibility({
                            column: column.id,
                            visibility: true,
                          })
                        }
                      />
                    )}
                  </Fragment>
                </li>
              )
          )}
        </ul>
      </BottomSheet>
      <ModalInformation
        isOpen={isOpenModalIndexInformation}
        onClickAction={handleClickModalIndexInformation}
      >
        <p>{t('overview.meter.detail.information.info')}</p>
      </ModalInformation>
    </section>
  );
}
